import type { AssignResult } from "./types";

/**
 * Solve a maximum-value assignment instance **exactly** with the Hungarian
 * method, off the main thread in a dedicated module Web Worker. Pure
 * TypeScript — no WASM download, no network — so the first solve is as warm
 * as the last.
 *
 * The returned status is always "OPTIMAL": the Hungarian method terminates
 * with matched dual potentials that certify the result (LP duality), so a
 * result is a proof, not a best-effort. There is no time limit to hit — at
 * the n ≤ ~hundreds a browser game uses, the O(n³) solve is microseconds.
 */

interface AssignOkMsg {
  id: number;
  ok: true;
  result: { total: number; assign: number[]; tSolveMs: number };
}
interface AssignErrMsg {
  id: number;
  ok: false;
  error: string;
}

let worker: Worker | null = null;
function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL("./exactAssign.worker.ts", import.meta.url), { type: "module" });
  }
  return worker;
}

let seq = 0;

export function solveAssignmentExact(values: number[][]): Promise<AssignResult> {
  const w = getWorker();
  const id = ++seq;
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      w.removeEventListener("message", onMsg);
      w.removeEventListener("error", onErr);
    };
    const onMsg = (e: MessageEvent<AssignOkMsg | AssignErrMsg>) => {
      const d = e.data;
      if (d.id !== id) return;
      cleanup();
      if (d.ok) {
        resolve({
          status: "OPTIMAL",
          total: d.result.total,
          assign: d.result.assign,
          tSolveMs: d.result.tSolveMs,
        });
      } else {
        reject(new Error(d.error || "Hungarian solver error"));
      }
    };
    const onErr = (e: ErrorEvent) => {
      cleanup();
      reject(new Error("Exact-assignment worker failed to load: " + (e.message || "unknown error")));
    };
    w.addEventListener("message", onMsg);
    w.addEventListener("error", onErr);
    w.postMessage({ id, values });
  });
}
